import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Transition } from '@headlessui/react'
import { MapPinIcon } from 'components/icons'
import DestinationSelectionPopoverItem from '../DestinationSelectionPopoverItem'
import { gql, useQuery } from 'urql'
import useDidMountEffect from 'data/hooks/useDidMount'
import { joinBy } from 'data/helpers/strings'
import { isServer } from 'data/helpers/ssr'
import SearchResults from './_SearchResults'
import { useDestinationSearch } from 'data/hooks/Destinations/useDestinationSearch'

const DestinationDropdownSearchItem = ({ value = '' }) => {
  return (
    <div className="flex flex-row items-center gap-20 mx-2 mt-2 antialiased border-transparent cursor-pointer mb-15 group-focus-visible:outline">
      <div className="flex items-center justify-center rounded-full w-30 h-30 bg-grey-150">
        <MapPinIcon />
      </div>
      <div className="font-bold text-14 text-grey-800 hover:underline">
        {value}
      </div>
    </div>
  )
}

DestinationDropdownSearchItem.propTypes = {
  value: PropTypes.string,
}

const DestinationSelectionPopover = ({
  open = false,
  setIsOpen = () => { },
  children,
  onClick = () => { },
  filter,
  stickyActive,
  disableAbsolute = false,
  marginOffset = true,
  onViewMore = () => { },
  wordpressSlugged = false,
}) => {
  const [viewAllOpen, setViewAllOpen] = useState(false)
  const { results: destinationSearchResults } = useDestinationSearch(filter)

  const [{ data, fetching }] = useQuery({
    query: gql`
      query DestinationSelectionPopoverData {
        countries(first: 100) {
          data {
            id
            value
            slug
          }
        }
        regions(first: 100) {
          data {
            id
            value
            slug
            country {
              id
              value
            }
          }
        }
        subRegions(first: 100) {
          data {
            id
            value
            slug
            region {
              id
              value
              country {
                id
                value
              }
            }
          }
        }
        featuredRegions(first: 4) {
          data {
            id
            value
            slug
            country {
              id
              value
              slug
            }
          }
        }
      }
    `,
    pause: isServer,
  })


  useDidMountEffect(() => {
    onViewMore()
  }, [viewAllOpen])

  if (isServer || fetching) {
    return null
  }

  return (
    <>
      {(open && !disableAbsolute) && <div className="fixed inset-0 p-0 m-0" onClick={() => setIsOpen(false)}></div>}
      <div className="h-full subpixel-antialiased" >
        {children}
        <Transition
          show={open}
          as={React.Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <div className={disableAbsolute
            ? '-mx-10'
            : `${stickyActive ? 'fixed' : 'absolute'} left-0 xl:absolute md:inset-x-0 bg-white z-10 shadow-public-searchbar ${viewAllOpen ? 'xl:max-w-[1175px] w-full xl:w-[110%]' : 'xl:max-w-[805px] w-full'} min-h-[350px] max-h-[500px] overflow-y-auto ${marginOffset ? 'xl:ml-50 md:mt-3 ' : ''}`
          }>

            <div className="py-30 px-25 md:px-40">
              {
                destinationSearchResults
                && <SearchResults
                  onClick={onClick}
                  wordpressSlugged={wordpressSlugged}
                  destinations={[
                    ...destinationSearchResults.cities.data,
                    ...destinationSearchResults.subRegions.data,
                    ...destinationSearchResults.regions.data,
                  ]}
                />
              }
              <div className="flex flex-col mb-10 md:flex-row">
                <div className="flex w-full max-w-[265px] flex-col gap-20">
                  <div className="text-grey-500 whitespace-nowrap text-14 leading-16">Popular Destinations</div>
                  {data?.featuredRegions?.data?.map((region, index) => (
                    <div key={index}>
                      <button
                        type="button"
                        className="flex flex-col gap-20 cursor-pointer"
                        onClick={() => onClick({
                          country: {
                            value: region.country.value,
                            slug: wordpressSlugged ? region.country.value : region.country.slug,
                          },
                          region: {
                            value: region.value,
                            slug: wordpressSlugged ? region.value : region.slug,
                          },
                          value: joinBy([region.value, region.country?.value]),
                        })}
                      >
                        <div className="flex flex-row items-center w-full gap-20">
                          <div className="flex items-center justify-center rounded-full w-30 h-30 bg-grey-150">
                            <MapPinIcon />
                          </div>
                          <div className="flex flex-col text-left">
                            <div className={`text-14 ${index == 0 ? 'text-primary' : null}`}>
                              {region.value}
                            </div>
                            <div className="truncate text-grey-500 text-12">
                              {data?.countries?.data?.filter(country => country.id == region.country.id)[0].value}
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="w-full mt-40 xl:mt-0">
                  <div className="flex justify-between w-full ">
                    <h3 className="text-grey-500 leading-16 text-14">All Destinations</h3>

                    <div>
                      <button
                        type="button"
                        onClick={() => setViewAllOpen(!viewAllOpen)}
                        className={`text-14 ${viewAllOpen ? 'text-grey-250' : 'text-primary'}`}
                      >
                        {viewAllOpen ? 'View Less' : 'View All'}
                      </button>
                    </div>
                  </div>
                  <DestinationSelectionPopoverItem wordpressSlugged={wordpressSlugged} selectRegion={(region) => onClick(region)} viewAll={viewAllOpen} />
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </>

  )
}

DestinationSelectionPopover.propTypes = {
  open: PropTypes.bool,
  setIsOpen: PropTypes.func,
  children: PropTypes.node,
  marginOffset: PropTypes.bool,
  onClick: PropTypes.func,
  stickyActive: PropTypes.bool,
  filter: PropTypes.string,
  disableAbsolute: PropTypes.bool,
  onViewMore: PropTypes.func,
  wordpressSlugged: PropTypes.bool,
}

export default DestinationSelectionPopover
